import React, { useEffect, useState } from "react";
import { Dropdown, Alert, Spinner, Form } from "react-bootstrap";
import { getPricing, createPrice, updatePricing, deletePricing } from "../../../../APIS";
import { emojis } from "../../bootstrap/alertData";

const Prices = ({ serviceId = 1 }) => {
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({ description: "", price: "", cloud_visibility: true, routeId: serviceId });

  // Alerts
  const [successMsg, setSuccessMsg] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);

  // ✅ Fetch Prices
  const fetchPrices = async () => {
    setLoading(true);
    try {
      const res = await getPricing(serviceId);
      setPrices(res.data.prices || []);
    } catch (error) {
      setErrorMsg("Failed to fetch prices ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (serviceId) fetchPrices();
  }, [serviceId]);

  // ✅ Handle Form Change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // ✅ Submit Form
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditMode) {
        await updatePricing(formData.id, formData);
        setSuccessMsg("Price updated ✅");
      } else {
        await createPrice(formData);
        setSuccessMsg("Price added ✅");
      }
      setShowForm(false);
      setIsEditMode(false);
      fetchPrices();
    } catch (error) {
      setErrorMsg("Error saving price ❌");
    }
  };

  // ✅ Delete Price
  const handleDelete = async (id) => {
    try {
      await deletePricing(id);
      setSuccessMsg("Price deleted ✅");
      fetchPrices();
    } catch (error) {
      setErrorMsg("Error deleting price ❌");
    }
  };

  // ✅ Toggle Cloud Visibility
  const handleToggleVisibility = async (price) => {
    try {
      const updated = { ...price, cloud_visibility: !price.cloud_visibility };
      await updatePricing(price.id, updated);
      setPrices((prev) =>
        prev.map((p) => (p.id === price.id ? updated : p))
      );
    } catch (error) {
      setErrorMsg("Error toggling visibility ❌");
    }
  };

  return (
    <div className="col-12">
      {/* Alerts */}
      {successMsg && <Alert variant="success" onClose={() => setSuccessMsg(null)} dismissible>{emojis.success} {successMsg}</Alert>}
      {errorMsg && <Alert variant="danger" onClose={() => setErrorMsg(null)} dismissible>{emojis.warning} {errorMsg}</Alert>}

      <div className="card">
        {!showForm ? (
          <>
            <div className="card-header d-flex justify-content-between align-items-center">
              <h4 className="card-title m-0">Route Prices</h4>
              <button
                className="btn btn-secondary btn-md"
                onClick={() => {
                  setFormData({ description: "", price: "", cloud_visibility: true, routeId: serviceId });
                  setIsEditMode(false);
                  setShowForm(true);
                }}
              >
                Add Price
              </button>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center p-4"><Spinner animation="border" /></div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Description</th>
                        <th>Price</th>
                        <th>Cloud Visible</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {prices.length > 0 ? prices.map((p, i) => (
                        <tr key={p.id}>
                          <td>{i + 1}</td>
                          <td>{p.description}</td>
                          <td>{p.price}</td>
                          <td>
                          <div className="form-check form-switch">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={p.cloud_visibility}
                                style={{cursor:"pointer"}}
                                onChange={() => handleToggleVisibility(p)}
                              />
                            </div>
                          </td>
                          <td>
                            <Dropdown>
                              <Dropdown.Toggle variant="link">&#x2022;&#x2022;&#x2022;</Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => { setFormData(p); setIsEditMode(true); setShowForm(true); }}>Edit</Dropdown.Item>
                                <Dropdown.Item onClick={() => handleDelete(p.id)}>Delete</Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="5" className="text-center">No prices found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="mb-3 row">
                <label className="col-lg-2 col-form-label">Description</label>
                <div className="col-lg-4">
                  <input
                    type="text"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g. Foreigner, Executive"
                    required
                  />
                </div>
              </div>
              <div className="mb-3 row">
                <label className="col-lg-2 col-form-label">Price</label>
                <div className="col-lg-4">
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g. 3000"
                    required
                  />
                </div>
              </div>
              <div className="mb-3 row">
                <label className="col-lg-2 col-form-label">Cloud Visible</label>
                <div className="col-lg-4">
                  <Form.Check
                    type="switch"
                    name="cloud_visibility"
                    checked={formData.cloud_visibility}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="mt-3">
                <button type="submit" className="btn btn-secondary">Save</button>
                <button type="button" className="btn btn-primary ms-2" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Prices;
